import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, Button, Avatar, Chip,
  TablePagination, Alert, Tabs, Tab, CircularProgress, Link
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_URL } from './config';
import { getAuthHeaders } from './commonfunctions/useAuthHelpers';

const ORANGE = '#F07828';
const ORANGE_2 = '#ed7f35ff';
const BG = '#F2F2F2';
const BORDER = 'rgba(25,118,210,0.20)';
const BLUE = '#1565C0';
const surfaceSx = { borderRadius: 2, backgroundColor: 'white', border: `1px solid ${BORDER}` };
const sectionSx = { ...surfaceSx, p: 2, position: 'relative', overflow: 'hidden', '&:before': { content: '""', position: 'absolute', top: 0, left: 0, bottom: 0, width: 6, background: `linear-gradient(180deg,${ORANGE},${ORANGE_2})` } };
const inputSx = { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: BORDER }, '&:hover fieldset': { borderColor: 'rgba(25,118,210,0.45)' }, '&.Mui-focused fieldset': { borderColor: 'rgba(240,120,40,0.80)' } } };
const primaryBtnSx = { borderRadius: 2, textTransform: 'none', fontWeight: 900, backgroundColor: ORANGE, '&:hover': { backgroundColor: ORANGE_2 } };
const outlinedBtnSx = { borderRadius: 2, textTransform: 'none', fontWeight: 800, borderColor: 'rgba(25,118,210,0.35)', color: BLUE, '&:hover': { borderColor: 'rgba(25,118,210,0.55)', backgroundColor: 'rgba(25,118,210,0.04)' } };
const tabsSx = { px: 1, borderBottom: `1px solid ${BORDER}`, minHeight: 48, '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 48 }, '& .Mui-selected': { color: `${ORANGE} !important` }, '& .MuiTabs-indicator': { backgroundColor: ORANGE, height: 3 } };

const photoUrl = (url) => url?.startsWith('http') ? url : url ? `${API_URL}${url}` : undefined;

function MentorCard({ mentor, connected = false, onConnect }) {
  const phone = mentor.MobileNumber;
  const email = mentor.Email;
  return (
    <Paper elevation={0} sx={{ ...surfaceSx, p: 2, borderTop: `4px solid ${ORANGE}` }}>
      <Stack direction="row" gap={2} alignItems="center">
        <Avatar src={photoUrl(mentor.PhotoURL)} sx={{ width: 60, height: 60 }}>
          {mentor.FullName?.[0]}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={900}>{mentor.FullName}</Typography>
          <Typography variant="body2">
            {mentor.Company || mentor.CurrentCompany || 'Company not listed'} · {mentor.CurrentCity || 'Location not listed'}
          </Typography>
        </Box>
      </Stack>
      <Typography sx={{ my: 1 }}>
        <strong>Role:</strong> {mentor.Role || 'Not provided'}
      </Typography>
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Chip label="Available to mentor" size="small" color="success" />
        {Boolean(Number(mentor.ReadyToHire)) && <Chip label="Ready to hire" size="small" color="primary" />}
      </Stack>
      <Typography variant="body2" sx={{mb:1}}><strong>Skills:</strong> {mentor.Skills || 'Not provided'}</Typography>
      {connected ? (
        <>
          <Chip label="Connected mentor" color="success" size="small" sx={{ mb: 1 }} />
          <Stack spacing={1} sx={{ mt: 1, overflowWrap: 'anywhere' }}>
            <Typography variant="body2">
              <strong>Email: </strong>
              {email ? <Link href={`mailto:${email}`}>{email}</Link> : 'Not provided'}
            </Typography>
            <Typography variant="body2">
              <strong>Mobile / WhatsApp: </strong>
              {phone ? <Link href={`tel:${phone}`}>{phone}</Link> : 'No verified number available'}
            </Typography>
          </Stack>
          {phone && <Button sx={{ ...outlinedBtnSx, mt: 2 }} variant="outlined" fullWidth
            component="a" target="_blank" rel="noopener noreferrer"
            href={`https://wa.me/${String(phone).replace(/\D/g, '')}`}>
            Message on WhatsApp
          </Button>}
        </>
      ) : (
        <>
          <Button fullWidth variant="contained" sx={{ ...primaryBtnSx, mt: 1 }}
            disabled={!!mentor.RequestStatus} onClick={() => onConnect(mentor.AlumniID)}>
            {mentor.RequestStatus ? `Request ${mentor.RequestStatus}` : 'Request connection'}
          </Button>
        </>
      )}
    </Paper>
  );
}

export default function StudentAlumniMentors() {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [myMentors, setMyMentors] = useState([]);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [skill, setSkill] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const url = tab === 1
      ? `${API_URL}/api/alumni/student/my-mentors`
      : `${API_URL}/api/alumni/student/mentors?${new URLSearchParams({ q, city, skill, page: page + 1, size })}`;
    fetch(url, { headers: getAuthHeaders(), credentials: 'include' })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw Error(data.error || 'Failed to load mentors');
        if (active) {
          if (tab === 1) setMyMentors(data.rows || []);
          else { setRows(data.rows || []); setTotal(data.total || 0); }
        }
      })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [tab, q, city, skill, page, size, refresh]);

  const connect = async alumniId => {
    try {
      setError('');
      const response = await fetch(`${API_URL}/api/alumni/student/connections`, {
        method: 'POST', credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ alumniId, message: 'I would like to connect for mentorship.' })
      });
      const data = await response.json();
      if (!response.ok) throw Error(data.error || 'Request failed');
      setMsg('Mentorship invitation sent. Awaiting mentor approval.');
      setRows(old => old.map(a => a.AlumniID === alumniId ? { ...a, RequestStatus: 'pending' } : a));
    } catch (err) { setError(err.message); }
  };

  return (
    <Box sx={{ p: 2, minHeight: '100vh', backgroundColor: BG }}>
      <Paper elevation={5} sx={{ mx: 'auto', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(25,118,210,0.15)' }}>
        <Box sx={{ px: 2, py: 1.5, background: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_2} 100%)`, color: 'white' }}>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 900 }}>Alumni Mentorship</Typography>
          <Typography sx={{ opacity: .9, fontSize: '.85rem' }}>Find alumni mentors and connect with those who accept your invitation.</Typography>
        </Box>
        <Box sx={{ p: 2, bgcolor: BG }}>
          <Paper elevation={0} sx={surfaceSx}>
            <Tabs value={tab} onChange={(_, value) => { setTab(value); setMsg(''); setError(''); }}
              variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={tabsSx}>
              <Tab label="Find Mentors" icon={<SchoolIcon />} iconPosition="start" />
              <Tab label="My Mentors" icon={<GroupsIcon />} iconPosition="start" />
            </Tabs>
            <Box sx={{ p: 2 }}>
              {error && <Alert sx={{ mb: 2 }} severity="error" onClose={() => setError('')}>{error}</Alert>}
              {msg && <Alert sx={{ mb: 2 }} severity="success" onClose={() => setMsg('')}>{msg}</Alert>}
              {tab === 0 && (
                <Paper elevation={0} sx={{ ...sectionSx, mb: 2 }}>
                  <Typography color={BLUE} fontWeight={900} mb={1.5}>Filters</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(180px,1fr))', md: 'repeat(3, minmax(180px,1fr))' }, gap: 1.5 }}>
                    <TextField size="small" fullWidth sx={inputSx} label="Name, company, branch or batch" value={q} onChange={e => { setQ(e.target.value); setPage(0); }} />
                    <TextField size="small" fullWidth sx={inputSx} label="City" value={city} onChange={e => { setCity(e.target.value); setPage(0); }} />
                    <TextField size="small" fullWidth sx={inputSx} label="Skills" value={skill} onChange={e => { setSkill(e.target.value); setPage(0); }} />
                  </Box>
                </Paper>
              )}
              {tab === 1 && (
                <Paper elevation={0} sx={{ ...sectionSx, mb: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                    <Typography fontWeight={900} color={BLUE}>My Mentors ({myMentors.length})</Typography>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} sx={outlinedBtnSx} onClick={() => setRefresh(v => v + 1)}>Refresh</Button>
                  </Stack>
                </Paper>
              )}
              <Paper elevation={0} sx={{ ...surfaceSx, overflow: 'hidden' }}>
                {loading ? <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress sx={{ color: ORANGE }} /></Box> : (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,270px),1fr))', gap: 2, p: 2 }}>
                      {(tab === 0 ? rows : myMentors).map(mentor => (
                        <MentorCard key={mentor.AlumniID} mentor={mentor} connected={tab === 1} onConnect={connect} />
                      ))}
                    </Box>
                    {(tab === 0 ? rows : myMentors).length === 0 && (
                      <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        {tab === 1 ? 'No accepted mentors yet. Once an alumnus accepts your invitation, their contact details will appear here.' : 'No available mentors found.'}
                      </Typography>
                    )}
                    {tab === 0 && <TablePagination component="div" count={total} page={page} rowsPerPage={size}
                      rowsPerPageOptions={[12, 24, 48]} onPageChange={(_, nextPage) => setPage(nextPage)}
                      onRowsPerPageChange={e => { setSize(+e.target.value); setPage(0); }} showFirstButton showLastButton />}
                  </>
                )}
              </Paper>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}
